import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addTableFilter,
  deleteTableFilter,
  getResourcePreference,
  getTableFilters,
  setResourcePreference,
  updateTableFilter,
} from "controller/api";
import { generateQueryKey } from "controller/cache";
import { produce } from "immer";
import { useEffect } from "react";
import type {
  AddTableFilterInput,
  ApiErrorType,
  HomeViewMode,
  ResourcePreference,
  SetPreferenceDataInput,
  UpdateTableFilterInput,
} from "shared";
import { sentryLogger } from "utilz";
import { debugConsole } from "utilz/helpers";

const PREFERENCE_QUERY_KEY = ["preference"];

/**
 * localStorage mirror of the home screen the user picked. Lives here rather than
 * with the screen because the cross-tab listener below has to know which key is
 * worth a refetch.
 */
export const HOME_VIEW_MODE_STORAGE_KEY = "home_view_mode";

export const useGetPreference = () => {
  return useQuery({
    queryKey: PREFERENCE_QUERY_KEY,
    queryFn: () => getResourcePreference(),
    onError: (err) => debugConsole(err),
    // The preference doc only changes through the mutations below, and every one
    // of them invalidates this key. Without this the query is stale on arrival,
    // so each new observer (the profile menu sits in the topbar on every route)
    // and each invalidate turns into another network round trip.
    staleTime: Infinity,
  });
};

/**
 * Persists which home screen the user picked. Kept separate from
 * `useSetPreference` because that one invalidates on success, which would make
 * the toggle wait on a refetch — here the cache is written optimistically so the
 * screen flips immediately, and rolls back if the server rejects it.
 */
export const useSetHomeViewModePreference = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mode: HomeViewMode) =>
      setResourcePreference({
        preferenceType: "home",
        data: { preference_value: "view_mode", settings: { mode } },
      }),
    onMutate: async (mode) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCE_QUERY_KEY });
      const previous =
        queryClient.getQueryData<ResourcePreference>(PREFERENCE_QUERY_KEY);
      queryClient.setQueryData<ResourcePreference>(
        PREFERENCE_QUERY_KEY,
        (old) =>
          produce(
            old ?? { success: true, key: "", settings: {} },
            (draft: ResourcePreference) => {
              if (!draft.settings) {
                draft.settings = {};
              }
              if (!draft.settings.home) {
                draft.settings.home = {};
              }
              draft.settings.home.view_mode = { mode };
            },
          ),
      );
      return { previous };
    },
    onError: (err, _mode, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PREFERENCE_QUERY_KEY, context.previous);
      }
      debugConsole(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PREFERENCE_QUERY_KEY });
    },
  });
};

/**
 * Refetches preferences when another tab writes the home view mode cache. The
 * other tab has already persisted the change server-side, so a refetch is all
 * this tab needs to catch up — no polling.
 *
 * The key check matters: other tabs write localStorage constantly (chat drafts
 * on every keystroke, time entry drafts, table column sizing), and an unfiltered
 * listener turns each of those into a preference refetch here.
 */
export const useSyncPreferenceAcrossTabs = () => {
  const queryClient = useQueryClient();
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      // `key` is null when the other tab called localStorage.clear() — on
      // sign-out, say — which does drop the mirrored mode.
      if (event.key !== null && event.key !== HOME_VIEW_MODE_STORAGE_KEY) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: PREFERENCE_QUERY_KEY });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [queryClient]);
};

export const useSetPreference = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SetPreferenceDataInput) => setResourcePreference(data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ["preference"],
      });
      // console.log({ data, variables, context });
      // queryClient.setQueryData(["preference"], (oldData: any) => {
      //   if (!oldData) {
      //     return data;
      //   }
      //   return produce(oldData, (draft: any) => {
      //     console.log(variables.data.settings);
      //     if (draft && draft?.settings && draft?.settings?.table) {
      //       draft.settings.table[variables.data.preference_value] =
      //         variables.data.settings;
      //     }
      //   });
      // });
    },
    onError: (err) => debugConsole(err),
  });
};

export const useAddTableFilter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddTableFilterInput) => addTableFilter(data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: generateQueryKey({ entity: "table-filters" }),
      });
    },
    onError: (err) => debugConsole(err),
  });
};

export const useUpdateTableFilter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateTableFilterInput) => updateTableFilter(data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: generateQueryKey({ entity: "table-filters" }),
      });
    },
    onError: (err) => debugConsole(err),
  });
};

export const useDeleteTableFilter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filterId: string) => deleteTableFilter(filterId),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: generateQueryKey({ entity: "table-filters" }),
      });
    },
    onError: (err) => debugConsole(err),
  });
};

export const useGetTableFilters = () => {
  return useQuery({
    queryKey: generateQueryKey({ entity: "table-filters" }),
    queryFn: () => getTableFilters(),
    onError: (error: ApiErrorType) => {
      debugConsole(error);
      sentryLogger({
        error,
        workflow: "ticket",
        functionName: "getTableFilters",
        message: "Failed to load ticket filter sidebar data",
      });
    },
    staleTime: Infinity,
    retry: 3,
    refetchOnMount: true,
  });
};
